import React, { useState, useEffect, useContext } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { usePermissionStore } from '../stores/permissionStore'
import { authApi } from '../lib/api/auth'
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Video,
  ClipboardList,
  BarChart3,
  Shield,
  User,
  Briefcase,
  Star,
  Menu,
  X,
  Lock,
  AlertCircle,
  Mail,
  ShieldCheck,
} from 'lucide-react'
import { ProfileCompletionCtx } from '../contexts/profileCompletion'

interface NavItem {
  icon: React.ElementType
  label: string
  path: string
}

// module key maps each admin nav item to its RBAC module name
const adminNavItems: (NavItem & { module: string })[] = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', module: 'dashboard' },
  { icon: Users, label: 'Users', path: '/admin/users', module: 'users' },
  { icon: Calendar, label: 'Sessions', path: '/admin/sessions', module: 'sessions' },
  { icon: FileText, label: 'Content', path: '/admin/content', module: 'content' },
  { icon: BarChart3, label: 'Reports', path: '/admin/reports', module: 'reports' },
  { icon: Shield, label: 'Security', path: '/admin/security', module: 'security' },
  { icon: Briefcase, label: 'Applications', path: '/admin/applications', module: 'applications' },
  { icon: Mail, label: 'Email Templates', path: '/admin/email-templates', module: 'email_templates' },
  { icon: ShieldCheck, label: 'Terms & Privacy', path: '/admin/terms-privacy', module: 'terms_privacy' },
  { icon: Settings, label: 'Settings', path: '/admin/settings', module: 'settings' },
]

const menuItems: Record<string, NavItem[]> = {
  admin: adminNavItems,
  client: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/client/home' },
    { icon: User, label: 'My Profile', path: '/client/profile' },
    { icon: Calendar, label: 'Schedule a Session', path: '/client/schedule' },
    { icon: Video, label: 'Manage Interviews', path: '/client/interviews' },
    { icon: FileText, label: 'View Feedback', path: '/client/feedback' },
  ],
  interviewer: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/interviewer' },
    { icon: Users, label: 'Clients', path: '/interviewer/clients' },
    { icon: Calendar, label: 'Schedule', path: '/interviewer/schedule' },
    { icon: ClipboardList, label: 'Evaluations', path: '/interviewer/evaluations' },
    { icon: Video, label: 'Recordings', path: '/interviewer/recordings' },
    { icon: Star, label: 'My Ratings', path: '/interviewer/ratings' },
    { icon: BarChart3, label: 'My Reports', path: '/interviewer/reports' },
    { icon: User, label: 'My Profile', path: '/interviewer/settings' },
  ],
}

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  client: 'Interview Candidate',
  interviewer: 'Interviewer',
}

interface LayoutProps {
  children: React.ReactNode
  role: 'client' | 'interviewer' | 'admin'
}

export default function Layout({ children, role }: LayoutProps) {
  const { user, logout } = useAuthStore()
  const { can, loaded: permsLoaded, is_super_admin } = usePermissionStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showProfileAlert, setShowProfileAlert] = useState(false)

  const { isComplete } = useContext(ProfileCompletionCtx)
  const profileLocked = role === 'client' && !isComplete

  useEffect(() => {
    setSidebarOpen(false)
    setShowProfileAlert(false)
  }, [location.pathname])

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch {
      // ignore logout API errors
    }
    logout()
    navigate('/signin')
  }

  // For admin role, filter nav items to only those the user can view.
  // While permissions are still loading, show all items (avoids flicker for super admins).
  const items = role === 'admin'
    ? adminNavItems.filter((item) => !permsLoaded || is_super_admin || can(item.module, 'view'))
    : (menuItems[role] || [])

  return (
    <div className="layout">
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        type="button"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className={`sidebar-header ${role === 'interviewer' || role === 'client' ? 'sidebar-header-interviewer' : ''}`}>
          <button
            type="button"
            className="sidebar-home-btn"
            onClick={() => navigate('/')}
            aria-label="Go to home"
          >
            <img
              src="/InterviewDressRehearsal_LOGO.png"
              alt="Interview Dress Rehearsal Logo"
              className="sidebar-logo"
            />
            {role !== 'interviewer' && role !== 'client' && (
              <div className="sidebar-brand">
                <span className="brand-name">Interview Dress Rehearsal</span>
                <span className="brand-role">{roleLabels[role]}</span>
              </div>
            )}
          </button>
          {(role === 'interviewer' || role === 'client') && user && (
            <div className="sidebar-header-userinfo">
              <span className="sidebar-header-name">{user.name}</span>
              <span className="sidebar-header-email">{user.email}</span>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {/* Profile incomplete banner inside nav */}
          {profileLocked && (
            <div className="profile-incomplete-badge">
              <AlertCircle size={13} />
              <span>Complete your profile to unlock all features</span>
            </div>
          )}

          {items.map((item) => {
            const isProfileItem = item.path === '/client/profile'
            const locked = profileLocked && !isProfileItem

            if (locked) {
              return (
                <button
                  key={item.path}
                  type="button"
                  className="nav-item nav-item-locked"
                  onClick={() => setShowProfileAlert(true)}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                  <Lock size={13} className="nav-lock-icon" />
                </button>
              )
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/client/home' || item.path === '/admin' || item.path === '/interviewer'}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'active' : ''}`
                }
              >
                <item.icon size={20} />
                <span>{item.label}</span>
                {isProfileItem && profileLocked && (
                  <span className="nav-profile-chip">Required</span>
                )}
              </NavLink>
            )
          })}

          {/* Alert shown when locked item is clicked */}
          {showProfileAlert && (
            <div className="nav-profile-alert">
              <AlertCircle size={14} />
              <span>Please complete your <strong>My Profile</strong> section for the best match and experience.</span>
              <button
                type="button"
                className="nav-alert-close"
                onClick={() => setShowProfileAlert(false)}
              >
                <X size={12} />
              </button>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="avatar">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name || 'Avatar'}
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                user?.name?.charAt(0) || 'U'
              )}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} type="button">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>

      <style>{`
        .layout {
          display: flex;
          min-height: 100vh;
          overflow-x: hidden;
        }

        .sidebar {
          width: 280px;
          background: #ffffff;
          color: var(--sidebar-text);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 100;
          border-right: 1px solid var(--sidebar-border, #e5e7eb);
          box-shadow: 1px 0 4px rgba(0, 0, 0, 0.04);
          /* Smooth sidebar on all sizes */
          transition: transform 0.3s ease, width 0.2s ease;
        }

        .sidebar-header {
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 1px solid var(--sidebar-border, #e5e7eb);
          flex-shrink: 0;
        }

        .sidebar-logo {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          flex-shrink: 0;
        }

        .sidebar-home-btn {
          display: flex;
          align-items: center;
          gap: 16px;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font: inherit;
          color: inherit;
          text-align: left;
          width: 100%;
        }
        .sidebar-home-btn:hover .brand-name { color: var(--accent-gold, #d4af37); }

        .sidebar-header-interviewer {
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 20px 16px;
        }
        .sidebar-header-interviewer .sidebar-home-btn {
          justify-content: center;
          width: auto;
        }
        .sidebar-header-interviewer .sidebar-logo {
          width: 56px;
          height: 56px;
          border-radius: 14px;
        }
        .sidebar-header-userinfo {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          line-height: 1.3;
          max-width: 100%;
          min-width: 0;
        }
        .sidebar-header-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--primary-navy, #1e2432);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        .sidebar-header-email {
          font-size: 12px;
          color: var(--text-muted, #6b7280);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .contractor-topbar {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding: 14px 28px 0;
        }
        .contractor-topbar-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          line-height: 1.25;
        }
        .contractor-topbar-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--primary-navy, #1e2432);
        }
        .contractor-topbar-email {
          font-size: 12px;
          color: var(--text-muted, #6b7280);
        }

        .sidebar-brand {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .brand-name {
          font-size: 18px;
          font-weight: 700;
          color: var(--primary-navy);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .brand-role {
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px 16px 24px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .profile-incomplete-badge {
          display: flex;
          align-items: flex-start;
          gap: 7px;
          padding: 10px 12px;
          background: rgba(234, 179, 8, 0.08);
          border: 1px solid rgba(234, 179, 8, 0.3);
          border-radius: 8px;
          font-size: 12px;
          color: #92400e;
          font-weight: 500;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .profile-incomplete-badge svg {
          flex-shrink: 0;
          margin-top: 1px;
          color: #d97706;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          color: var(--text-muted);
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          /* Touch-friendly */
          min-height: 44px;
        }

        .nav-item:hover {
          background: var(--sidebar-hover, #f3f4f6);
          color: var(--sidebar-text, #374151);
        }

        .nav-item.active {
          background: var(--sidebar-active, rgba(45, 55, 72, 0.08));
          color: var(--sidebar-active-text, #2d3748);
          font-weight: 600;
        }

        .nav-item svg {
          flex-shrink: 0;
        }

        .nav-item-locked {
          opacity: 0.45;
          cursor: not-allowed;
          position: relative;
        }

        .nav-item-locked:hover {
          background: rgba(234, 179, 8, 0.06);
          opacity: 0.6;
        }

        .nav-lock-icon {
          margin-left: auto;
          color: #d97706 !important;
          opacity: 1 !important;
        }

        .nav-profile-chip {
          margin-left: auto;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 7px;
          border-radius: 20px;
          background: rgba(234, 179, 8, 0.15);
          color: #92400e;
          letter-spacing: 0.4px;
          text-transform: uppercase;
        }

        .nav-profile-alert {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 12px 14px;
          background: rgba(220, 38, 38, 0.06);
          border: 1px solid rgba(220, 38, 38, 0.2);
          border-radius: 10px;
          font-size: 12.5px;
          color: #991b1b;
          line-height: 1.5;
          margin-top: 6px;
          position: relative;
        }

        .nav-profile-alert svg {
          flex-shrink: 0;
          margin-top: 1px;
          color: #dc2626;
        }

        .nav-alert-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: #991b1b;
          margin-left: auto;
          flex-shrink: 0;
          opacity: 0.7;
          line-height: 1;
          min-width: 28px;
          min-height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-alert-close:hover { opacity: 1; }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--sidebar-border, #e5e7eb);
          flex-shrink: 0;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--page-bg);
          border-radius: 10px;
          margin-bottom: 12px;
          min-width: 0;
        }

        .user-details {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 10px;
          color: var(--text-muted);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          /* Touch-friendly */
          min-height: 44px;
        }

        .logout-btn:hover {
          background: rgba(229, 62, 62, 0.06);
          border-color: rgba(229, 62, 62, 0.3);
          color: var(--danger);
        }

        .main-content {
          flex: 1;
          margin-left: 280px;
          background: var(--page-bg);
          min-height: 100vh;
          padding: 32px;
          /* Prevent content from going too wide on very large screens */
          max-width: calc(100% - 280px);
        }

        .mobile-menu-btn {
          display: none;
          position: fixed;
          top: 16px;
          left: 16px;
          z-index: 200;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: #ffffff;
          color: var(--primary-navy);
          border: 1px solid var(--border);
          cursor: pointer;
          align-items: center;
          justify-content: center;
          box-shadow: var(--shadow);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 90;
          /* Smooth fade */
          backdrop-filter: blur(2px);
        }

        /* ── Tablet (769–1024px): narrower sidebar ─── */
        @media (max-width: 1024px) {
          .sidebar {
            width: 240px;
          }
          .main-content {
            margin-left: 240px;
            max-width: calc(100% - 240px);
            padding: 24px;
          }
          .brand-name { font-size: 16px; }
        }

        /* ── Mobile (≤768px): slide-out drawer ───────── */
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex;
          }
          .sidebar-overlay {
            display: block;
          }
          .sidebar {
            width: 280px;
            transform: translateX(-100%);
          }
          .sidebar.open {
            transform: translateX(0);
            box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
          }
          .main-content {
            margin-left: 0;
            max-width: 100%;
            padding: 72px 16px 24px 16px;
          }
          .sidebar-header {
            padding: 16px 20px;
          }
          .sidebar-logo {
            width: 40px;
            height: 40px;
          }
          .brand-name { font-size: 16px; }
          .nav-item { padding: 13px 16px; font-size: 15px; }
        }

        /* ── Small mobile (≤480px) ──────────────────── */
        @media (max-width: 480px) {
          .sidebar {
            width: 100%;
            max-width: 300px;
          }
          .mobile-menu-btn {
            top: 12px;
            left: 12px;
            width: 42px;
            height: 42px;
          }
          .main-content {
            padding: 68px 14px 20px 14px;
          }
          .sidebar-header {
            padding: 14px 16px;
          }
          .user-info { padding: 10px; }
          .user-name { font-size: 13px; }
          .user-email { font-size: 11px; }
          .logout-btn { padding: 10px 14px; font-size: 13px; }
        }

        /* ── Very small mobile (≤360px) ─────────────── */
        @media (max-width: 360px) {
          .main-content {
            padding: 64px 12px 16px 12px;
          }
          .nav-item { padding: 12px 14px; font-size: 14px; }
          .brand-name { font-size: 15px; }
        }
      `}</style>
    </div>
  )
}
